import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { GALLERY, GALLERY_CATEGORIES } from "../data/content";

export default function GallerySection({ heading = true }) {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = GALLERY.filter((g) => filter === "All" || g.category === filter);

  const open = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const next = (e) => {
    e.stopPropagation();
    setLightbox((p) => (p + 1) % items.length);
  };
  const prev = (e) => {
    e.stopPropagation();
    setLightbox((p) => (p - 1 + items.length) % items.length);
  };

  return (
    <section data-testid="gallery-section" className="section-y relative">
      <div className="container-x">
        {heading && (
          <Reveal>
            <span className="overline">The Gallery</span>
            <h2 className="mt-5 font-playfair text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Moments from our world
            </h2>
          </Reveal>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          {GALLERY_CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`rounded-full border px-5 py-2 text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                filter === c
                  ? "border-gold bg-gold text-black"
                  : "border-white/10 text-neutral-300 hover:border-white/30"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
          <AnimatePresence>
            {items.map((g, i) => (
              <motion.button
                key={g.src}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                onClick={() => open(i)}
                data-testid={`gallery-item-${i}`}
                className="group relative block w-full overflow-hidden rounded-xl border border-white/5"
              >
                <img
                  src={g.src}
                  alt={g.category}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <span className="p-4 text-xs uppercase tracking-[0.2em] text-gold">
                    {g.category}
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 p-6"
          >
            <button onClick={close} data-testid="lightbox-close" className="absolute right-6 top-6 text-white/70 hover:text-white">
              <X size={30} />
            </button>
            <button onClick={prev} className="absolute left-4 text-white/60 hover:text-gold md:left-10">
              <ChevronLeft size={40} />
            </button>
            <motion.img
              key={items[lightbox].src}
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={items[lightbox].src}
              alt=""
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full rounded-xl object-contain"
            />
            <button onClick={next} className="absolute right-4 text-white/60 hover:text-gold md:right-10">
              <ChevronRight size={40} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
